import { apiFetch } from '../lib/apiFetch';

export interface CustomerLocation {
  id: string;
  customer_id: string;
  name: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  created_at: string | null;
}

export interface CustomerLocationInput {
  name?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

async function parseJsonOrThrow(res: Response, fallback: string): Promise<any> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || fallback);
  return body;
}

export async function fetchCustomerLocations(customerId: string): Promise<CustomerLocation[]> {
  const res = await apiFetch(`/customers/${customerId}/locations`);
  const body = await parseJsonOrThrow(res, 'Failed to load locations');
  return body.data || [];
}

export interface CustomerLocationWithCustomer extends CustomerLocation {
  customer_name: string;
  customer_kind: 'individual' | 'company';
}

export async function fetchAllCustomerLocations(): Promise<CustomerLocationWithCustomer[]> {
  const res = await apiFetch('/customer-locations');
  const body = await parseJsonOrThrow(res, 'Failed to load customer locations');
  return body.data || [];
}

export async function addCustomerLocation(
  customerId: string,
  input: CustomerLocationInput,
): Promise<CustomerLocation> {
  const res = await apiFetch(`/customers/${customerId}/locations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = await parseJsonOrThrow(res, 'Failed to add location');
  return body.data;
}

export async function updateCustomerLocation(
  customerId: string,
  locationId: string,
  input: CustomerLocationInput,
): Promise<CustomerLocation> {
  const res = await apiFetch(`/customers/${customerId}/locations/${locationId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = await parseJsonOrThrow(res, 'Failed to update location');
  return body.data;
}

export async function deleteCustomerLocation(
  customerId: string,
  locationId: string,
): Promise<void> {
  const res = await apiFetch(`/customers/${customerId}/locations/${locationId}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || 'Failed to delete location');
  }
}
